import { computed } from '@vue/composition-api';

/**
 * Determines the wireless device type based on serial number range
 */
export function getWirelessDeviceType(serialNumber) {
  if (!serialNumber || serialNumber === '00000000') {
    return 'UNKNOWN';
  }

  const serial = parseInt(serialNumber, 10);

  if (isNaN(serial)) {
    return 'UNKNOWN';
  }

  if (serial >= 1000000 && serial <= 1999999) {
    return 'UNIVERSAL';
  } else if (serial >= 2000000 && serial <= 2999999) {
    return 'PIR';
  } else if (serial >= 3000000 && serial <= 3999999) {
    return 'GLASSBREAK';
  } else if (serial >= 4000000 && serial <= 4999999) {
    return 'HOLDUP';
  } else if (serial >= 5000000 && serial <= 5999999) {
    return 'KEYFOB';
  } else if (serial >= 6000000 && serial <= 7999999) {
    return 'SMOKE_CO';
  } else if (serial >= 8000000 && serial <= 8999999) {
    return 'FOUR_ZONE_EXPANDER';
  } else if (serial >= 9000000 && serial <= 9999999) {
    return 'WIRELESS_PIR';
  } else if (serial >= 13000000 && serial <= 13999999) {
    return 'REPEATER_1100R';
  }

  return 'UNKNOWN';
}

export function useZoneFieldsEnabled(zoneType) {
  /**
   * STANDARD TAB
   */
  const standardFields = computed(() => {
    const type = zoneType.value;

    const defaults = {
      zoneNumber: true,
      zoneName: true,
      zoneType: true,
      areaNumber: true,
      followArea: true,
      armedAreas: true,
      expanderSerialNumber: true,
    };

    switch (type) {
      case 'BLANK': // Blank
        return {
          ...defaults,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };

      case 'NIGHT':
        return {
          ...defaults,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'INSTANT':
        return {
          ...defaults,
          armedAreas: false,
          expanderSerialNumber: false
        };

      case 'DAY':
        return {
          ...defaults,
          armedAreas: false,
          expanderSerialNumber: false
        };

      case 'EXIT':
        return {
          ...defaults,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'FIRE':
        return {
          ...defaults,
          areaNumber: false,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'PANIC':
        return {
          ...defaults,
          areaNumber: false,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'EMERGENCY':
        return {
          ...defaults,
          areaNumber: false,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'SUPERVISORY':
        return {
          ...defaults,
          areaNumber: false,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'AUXILIARY1':
        return {
          ...defaults,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'AUXILIARY2':
        return {
          ...defaults,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'FIRE_VERIFY':
        return {
          ...defaults,
          areaNumber: false,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };

      case 'ARMING':
        return {
          ...defaults,
          areaNumber: false,
          followArea: false,
          expanderSerialNumber: false
        };
      case 'CARBON_MONOXIDE':
        return {
          ...defaults,
          areaNumber: false,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };
      case 'DOOR_BELL':
        return {
          ...defaults,
          followArea: false,
          armedAreas: false,
          expanderSerialNumber: false
        };

      default:
        return defaults;
    }
  });

  /**
   * ACTION TAB
   */
  const actionFields = computed(() => {
    const type = zoneType.value;

    const defaults = {
      doMessage: true,
      doOutputFireBell: true,
      doOutputAction: true,
      dsMessage: true,
      dsOutput: true,
      dsOutputAction: true,
      aoMessage: true,
      aoOutput: true,
      aoOutputAction: true,
      asMessage: true,
      asOutput: true,
      asOutputAction: true,
    };

    switch (type) {
      case 'FIRE':
        return {
          ...defaults,
          doMessage: false,
          doOutputAction: false,
          dsMessage: false,
          dsOutput: false,
          dsOutputAction: false,
        };

      case 'PANIC':
      case 'EMERGENCY':
      case 'SUPERVISORY':
        return {
          ...defaults,
          doMessage: false,
          doOutputFireBell: false,
          doOutputAction: false,
          dsMessage: false,
          dsOutput: false,
          dsOutputAction: false,
        };

      case 'FIRE_VERIFY':
        return {
          ...defaults,
          doMessage: false,
          doOutputAction: false,
          dsMessage: false,
          dsOutput: false,
          dsOutputAction: false,
        };

      case 'ARMING':
        // Todos los campos deshabilitados
        return {
          doMessage: false,
          doOutputFireBell: false,
          doOutputAction: false,
          dsMessage: false,
          dsOutput: false,
          dsOutputAction: false,
          aoMessage: false,
          aoOutput: false,
          aoOutputAction: false,
          asMessage: false,
          asOutput: false,
          asOutputAction: false,
        };

      default:
        return defaults;
    }
  });

  /**
   * ADVANCED TAB
   */
  const advancedFields = computed(() => {
    const type = zoneType.value;

    const defaults = {
      swingerBypass: true,
      zoneRetard: true,
      fastResponse: true,
      crossZone: true,
      priority: true,
      firePanelSlaveInput: true,
      zoneRealTimeStatus: true,
      trafficCount: true,
      lockdown: true,
      zoneAuditDays: true,
      reportWithAccountNumber: true,
      chimeSong: true,
      prewarPresignal: true,
      entryDelayNumber: true,
    };

    switch (type) {
      case 'BLANK': // Blank
        return {
          swingerBypass: true,
          zoneRetard: false,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: false,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'NIGHT':
        return {
          swingerBypass: true,
          zoneRetard: false,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: true,
          trafficCount: true,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: false,
          chimeSong: true,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'INSTANT':
        return {
          swingerBypass: true,
          zoneRetard: false,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: true,
          trafficCount: true,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: false,
          chimeSong: true,
          prewarPresignal: false,
          entryDelayNumber: false,
        };


      case 'DAY':
        return {
          swingerBypass: true,
          zoneRetard: false,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: true,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: false,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };


      case 'EXIT':
        return {
          swingerBypass: true,
          zoneRetard: false,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: true,
          trafficCount: true,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: false,
          chimeSong: true,
          prewarPresignal: true,
          entryDelayNumber: true,
        };

      case 'FIRE':
        return {
          swingerBypass: true,
          zoneRetard: true,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: true,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: false,
          reportWithAccountNumber: true,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'PANIC':
        return {
          swingerBypass: true,
          zoneRetard: true,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: true,
          zoneAuditDays: true,
          reportWithAccountNumber: true,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'EMERGENCY':
        return {
          swingerBypass: true,
          zoneRetard: false,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: true,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'SUPERVISORY':
        return {
          swingerBypass: true,
          zoneRetard: true,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: true,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'AUXILIARY1':
      case 'AUXILIARY2':
        return {
          swingerBypass: true,
          zoneRetard: true,
          fastResponse: true,
          crossZone: true,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: true,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: false,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'FIRE_VERIFY':
        return {
          swingerBypass: true,
          zoneRetard: false,
          fastResponse: true,
          crossZone: false,
          priority: true,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: false,
          reportWithAccountNumber: true,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };

      case 'ARMING':
        return {
          swingerBypass: false,
          zoneRetard: false,
          fastResponse: false,
          crossZone: false,
          priority: false,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: false,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };
      case 'CARBONE_MONOXIDE':
        return {
          swingerBypass: false,
          zoneRetard: false,
          fastResponse: false,
          crossZone: false,
          priority: false,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: false,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: true,
          reportWithAccountNumber: true,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };
      case 'DOOR_BELL':
        return {
          swingerBypass: false,
          zoneRetard: false,
          fastResponse: true,
          crossZone: false,
          priority: false,
          firePanelSlaveInput: false,
          zoneRealTimeStatus: true,
          trafficCount: false,
          lockdown: false,
          zoneAuditDays: false,
          reportWithAccountNumber: false,
          chimeSong: false,
          prewarPresignal: false,
          entryDelayNumber: false,
        };
      default:
        return defaults;
    }
  });

  return {
    standardFields,
    actionFields,
    advancedFields,
  };
}

/**
 * Returns which wireless fields should be enabled based on device type and wireless status
 */
export function useWirelessFieldsEnabled(isWireless, serialNumber) {
  const deviceType = computed(() => getWirelessDeviceType(serialNumber.value));

  const wirelessFields = computed(() => {
    // If wireless is disabled, all fields are disabled
    if (!isWireless.value || isWireless.value === 'N') {
      return {
        dmpWirelessSerial: false,
        dmpWirelessContact: false,
        normallyOpen: false,
        dmpWirelessSupervision: false,
        ledOperation: false,
        disarmDisable: false,
        dmpWirelessPirPulseCount: false,
        dmpWirelessPirPulseSensitivity: false,
        petImmunity: false,
      };
    }

    const type = deviceType.value;

    // Base configuration - all disabled
    const allDisabled = {
      dmpWirelessSerial: true, // Always enabled when wireless is active
      dmpWirelessContact: false,
      normallyOpen: false,
      dmpWirelessSupervision: true, // Common for most types
      ledOperation: false,
      disarmDisable: false,
      dmpWirelessPirPulseCount: false,
      dmpWirelessPirPulseSensitivity: false,
      petImmunity: false,
    };

    switch (type) {
      case 'UNKNOWN':
        return {
          ...allDisabled,
          dmpWirelessSerial: true,
          dmpWirelessContact: true,
          dmpWirelessSupervision: true,
        };

      case 'UNIVERSAL':
        return {
          ...allDisabled,
          dmpWirelessSerial: true,
          dmpWirelessContact: true,
          normallyOpen: true,
          dmpWirelessSupervision: true,
        };

      case 'PIR':
      case 'GLASSBREAK':
      case 'SMOKE_CO':
      case 'REPEATER_1100R':
        return {
          ...allDisabled,
          dmpWirelessSerial: true,
          dmpWirelessSupervision: true,
        };

      case 'HOLDUP':
        return {
          ...allDisabled,
          dmpWirelessSerial: true,
          dmpWirelessSupervision: true,
          ledOperation: true,
        };

      case 'KEYFOB':
        // Keyfob cannot be programmed, only serial enabled
        return {
          ...allDisabled,
          dmpWirelessSerial: true,
          dmpWirelessContact: false,
          dmpWirelessSupervision: false,
        };

      case 'FOUR_ZONE_EXPANDER':
        return {
          ...allDisabled,
          dmpWirelessSerial: true,
          dmpWirelessContact: true,
          dmpWirelessSupervision: true,
        };

      case 'WIRELESS_PIR':
        return {
          ...allDisabled,
          dmpWirelessSerial: true,
          dmpWirelessSupervision: true,
          dmpWirelessPirPulseCount: true,
          dmpWirelessPirPulseSensitivity: true,
        };

      default:
        return allDisabled;
    }
  });

  return {
    deviceType,
    wirelessFields,
  };
}
